import {
  Activity,
  Bell,
  Briefcase,
  Building2,
  CalendarPlus,
  Car,
  CarFront,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  Handshake,
  Layers,
  LayoutDashboard,
  Megaphone,
  Package,
  Percent,
  Settings,
  ShieldCheck,
  UserCog,
  Users,
  Users2,
  Wallet,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { NavLink } from '@/components/NavLink'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useAbility } from '@/contexts/AbilityContext'
import { useIsMobile } from '@/hooks/useMobile'
import type { Subjects } from '@/libs/ability'
import { APP_ROUTES } from '@/libs/constants'
import { cn } from '@/libs/utils'
interface AppSidebarProps {
  mobileOpen?: boolean
  onMobileOpenChange?: (open: boolean) => void
  variant?: 'app' | 'admin'
}

interface NavItem {
  title: string
  url: string
  icon: React.ElementType
  tooltipKey: string
  subject: string
  order: number
  end?: boolean
  ariaLabel?: string
}

const getSidebarNavLinkClassName = (expanded: boolean, isActive: boolean) =>
  cn(
    'flex items-center gap-2.5 px-2.5 py-1.5 md:py-2 rounded-md transition-all duration-200 text-[13px]',
    'text-sidebar-foreground/90 hover:text-sidebar-foreground hover:bg-sidebar-accent',
    // Icons carry the wayfinding — keep them at full contrast
    // regardless of label opacity, and give them a slightly
    // heavier stroke so they read clearly on the dark surface.
    '[&_svg]:text-sidebar-foreground [&_svg]:opacity-100 [&_svg]:stroke-[2.25]',
    'hover:[&_svg]:text-sidebar-primary',
    !expanded && 'justify-center',
    isActive && 'bg-sidebar-accent text-sidebar-primary font-medium [&_svg]:text-sidebar-primary',
  )

interface SidebarNavLinkProps {
  item: NavItem
  expanded: boolean
  isActive: boolean
  isSheet: boolean
  onNavigate: () => void
}

function SidebarNavLink({ item, expanded, isActive, isSheet, onNavigate }: SidebarNavLinkProps) {
  const { t } = useTranslation()
  const className = getSidebarNavLinkClassName(expanded, isActive)

  if (!expanded) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink
            to={item.url}
            end={item.end ?? item.url === '/'}
            onClick={() => isSheet && onNavigate()}
            className={className}
            aria-label={item.title}
          >
            <item.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
          </NavLink>
        </TooltipTrigger>
        <TooltipContent side="right">{t(item.tooltipKey)}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <NavLink
      to={item.url}
      end={item.end ?? item.url === '/'}
      onClick={() => isSheet && onNavigate()}
      className={className}
    >
      <item.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span className="whitespace-nowrap">{item.title}</span>
    </NavLink>
  )
}

interface SidebarContentProps {
  isSheet?: boolean
  showExpanded: boolean
  navItems: NavItem[]
  currentPathname: string
  collapsed: boolean
  onNavigateHome: () => void
  onCloseMobile: () => void
  onToggleCollapse: () => void
}

function SidebarContent({
  isSheet = false,
  showExpanded,
  navItems,
  currentPathname,
  collapsed,
  onNavigateHome,
  onCloseMobile,
  onToggleCollapse,
}: SidebarContentProps) {
  const { t } = useTranslation()
  const expanded = showExpanded || isSheet

  return (
    <>
      {/* Header */}
      <header
        className="h-14 md:h-16 px-2 md:px-3 flex items-center justify-between bg-sidebar shrink-0"
        data-tour="sidebar-logo"
      >
        <button
          onClick={onNavigateHome}
          className={cn(
            'flex items-center gap-2 rounded-lg transition-all duration-200 hover:opacity-90 min-w-0 overflow-hidden bg-white shadow-sm',
            expanded ? 'flex-1 justify-center px-2 py-1.5' : 'w-10 h-10 justify-center p-1',
          )}
          aria-label={t('nav.homeAriaLabel')}
        >
          {expanded ? (
            <img
              src="/logo_.png"
              alt={t('common.appName')}
              className="h-8 w-auto max-w-full object-contain"
            />
          ) : (
            <div
              aria-hidden="true"
              className="w-8 h-8"
              style={{
                backgroundImage: `url(/logo_.png)`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'left center',
                // Logo is 844x180; emblem ~ first 180px. Scale so 32px height -> 32 * 844/180 ≈ 150px wide bg.
                backgroundSize: 'auto 100%',
              }}
            />
          )}
        </button>
      </header>

      {/* Navigation */}
      <nav
        className="flex-1 p-1.5 md:p-2 space-y-0.5 overflow-y-auto"
        aria-label={t('nav.primaryNavigation')}
        data-tour="sidebar-nav"
      >
        {navItems.map((item) => {
          const isActive = item.end
            ? currentPathname === item.url
            : currentPathname.startsWith(item.url)
          return (
            <SidebarNavLink
              key={item.url}
              item={item}
              expanded={expanded}
              isActive={isActive}
              isSheet={isSheet}
              onNavigate={onCloseMobile}
            />
          )
        })}
      </nav>

      {/* Collapse toggle */}
      {!isSheet && (
        <button
          onClick={onToggleCollapse}
          className="flex items-center justify-center h-12 border-t border-sidebar-border text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors [&_svg]:stroke-[2.25]"
          aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      )}
    </>
  )
}

export function AppSidebar({
  mobileOpen: controlledMobileOpen,
  onMobileOpenChange,
  variant = 'app',
}: AppSidebarProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(false)
  const [userCollapsed, setUserCollapsed] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [internalMobileOpen, setInternalMobileOpen] = useState(false)
  const isMobile = useIsMobile()

  const mobileOpen = controlledMobileOpen ?? internalMobileOpen
  const setMobileOpen = onMobileOpenChange ?? setInternalMobileOpen

  const isSettingsPage = location.pathname.startsWith(APP_ROUTES.settings.base)

  // Auto-collapse on settings page
  useEffect(() => {
    if (variant === 'app' && isSettingsPage) {
      setCollapsed(true)
    } else {
      setCollapsed(userCollapsed)
    }
  }, [isSettingsPage, userCollapsed, variant])

  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false)
    }
  }, [isMobile, setMobileOpen])
  const ability = useAbility()
  // `order` is configurable in future (e.g. via settings/user prefs).
  // Items are rendered sorted by ascending `order`.

  const appNavItems: NavItem[] = [
    {
      title: t('nav.scheduler'),
      url: APP_ROUTES.booking.base,
      icon: CalendarPlus,
      tooltipKey: 'tooltips.nav.booking',
      subject: 'Job',
      order: 1,
    },
    {
      title: t('nav.customers'),
      url: APP_ROUTES.customer.base,
      icon: Users,
      tooltipKey: 'tooltips.nav.customers',
      subject: 'Customer',
      order: 2,
    },
    {
      title: t('nav.vehicles'),
      url: APP_ROUTES.vehicle.base,
      icon: Car,
      tooltipKey: 'tooltips.nav.vehicles',
      subject: 'Vehicle',
      order: 3,
    },
    {
      title: t('nav.jobs'),
      url: APP_ROUTES.job.base,
      icon: ClipboardList,
      tooltipKey: 'tooltips.nav.jobs',
      subject: 'Job',
      order: 4,
    },
    {
      title: t('nav.myWorkspace'),
      url: '/my-workspace',
      icon: Briefcase,
      tooltipKey: 'tooltips.nav.myWorkspace',
      subject: 'WorkLog',
      order: 5,
    },
    {
      title: t('nav.teamOverview'),
      url: '/team-overview',
      icon: Users2,
      tooltipKey: 'tooltips.nav.teamOverview',
      subject: 'WorkLog',
      order: 6,
    },
    {
      title: t('nav.billing'),
      url: '/invoicing',
      icon: FileText,
      tooltipKey: 'tooltips.nav.billing',
      subject: 'Billing',
      order: 7,
    },
    {
      title: t('nav.economy'),
      url: '/economy',
      icon: Wallet,
      tooltipKey: 'tooltips.nav.economy',
      subject: 'Billing',
      order: 8,
    },
    {
      title: t('nav.products'),
      url: APP_ROUTES.product.base,
      icon: Package,
      tooltipKey: 'tooltips.nav.products',
      subject: 'Product',
      order: 9,
    },
    {
      title: t('nav.rentalFleet'),
      url: APP_ROUTES.rentCar.base,
      icon: CarFront,
      tooltipKey: 'tooltips.nav.rentalFleet',
      subject: 'LoanCar',
      order: 10,
    },
    {
      title: t('nav.campaigns'),
      url: '/campaigns',
      icon: Megaphone,
      tooltipKey: 'tooltips.nav.campaigns',
      subject: 'Campaign',
      order: 11,
    },
    {
      title: t('nav.settings'),
      url: APP_ROUTES.settings.base,
      icon: Settings,
      tooltipKey: 'tooltips.nav.settings',
      subject: 'Settings',
      order: 12,
    },
  ].sort((a, b) => a.order - b.order)

  const adminNavItems: NavItem[] = [
    {
      title: t('admin.nav.overview', 'Overview'),
      url: APP_ROUTES.admin.base,
      icon: LayoutDashboard,
      tooltipKey: 'admin.nav.overview',
      subject: 'Admin',
      order: 1,
      end: true,
    },
    {
      title: t('admin.nav.orgs', 'Organizations'),
      url: APP_ROUTES.admin.organizations,
      icon: Building2,
      tooltipKey: 'admin.nav.orgs',
      subject: 'Admin',
      order: 2,
    },
    {
      title: t('admin.nav.subs', 'Subscriptions'),
      url: APP_ROUTES.admin.subscriptions,
      icon: Wallet,
      tooltipKey: 'admin.nav.subs',
      subject: 'Admin',
      order: 3,
    },
    {
      title: t('admin.nav.features', 'Features'),
      url: APP_ROUTES.admin.features,
      icon: Layers,
      tooltipKey: 'admin.nav.features',
      subject: 'Admin',
      order: 4,
    },
    {
      title: t('admin.nav.usage', 'Usage'),
      url: APP_ROUTES.admin.usage,
      icon: Activity,
      tooltipKey: 'admin.nav.usage',
      subject: 'Admin',
      order: 5,
    },
    {
      title: t('admin.nav.discounts', 'Discounts'),
      url: APP_ROUTES.admin.discounts,
      icon: Percent,
      tooltipKey: 'admin.nav.discounts',
      subject: 'Admin',
      order: 6,
    },
    {
      title: t('admin.nav.roles', 'Global roles'),
      url: APP_ROUTES.admin.globalRoles,
      icon: ShieldCheck,
      tooltipKey: 'admin.nav.roles',
      subject: 'Admin',
      order: 7,
    },
    {
      title: t('admin.nav.automations', 'Communication automations'),
      url: APP_ROUTES.admin.automations,
      icon: Bell,
      tooltipKey: 'admin.nav.automations',
      subject: 'Admin',
      order: 8,
    },
    {
      title: t('admin.nav.userRoles', 'User role assignments'),
      url: APP_ROUTES.admin.users,
      icon: UserCog,
      tooltipKey: 'admin.nav.userRoles',
      subject: 'Admin',
      order: 9,
    },
    {
      title: t('admin.nav.salesCompanies', 'Sales companies'),
      url: APP_ROUTES.admin.salesCompanies,
      icon: Handshake,
      tooltipKey: 'admin.nav.salesCompanies',
      subject: 'Admin',
      order: 10,
    },
  ]

  const navItems =
    variant === 'admin'
      ? adminNavItems
      : appNavItems.filter((item) => ability.can('read', item.subject as Subjects))
  const showExpanded = !collapsed || hovered
  const showCollapsedWidth = collapsed && !hovered
  const sidebarWidthClassName = showCollapsedWidth ? 'w-14' : 'w-52 lg:w-56'

  const handleNavigateHome = () =>
    navigate(variant === 'admin' ? APP_ROUTES.admin.base : APP_ROUTES.home)
  const handleCloseMobile = () => setMobileOpen(false)
  const handleToggleCollapse = () => {
    const next = !collapsed
    setCollapsed(next)
    setUserCollapsed(next)
  }

  // Mobile sidebar (Sheet)
  if (isMobile) {
    return (
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="w-[280px] p-0 bg-sidebar text-sidebar-foreground border-sidebar-border"
        >
          <SidebarContent
            isSheet
            showExpanded={showExpanded}
            navItems={navItems}
            currentPathname={location.pathname}
            collapsed={collapsed}
            onNavigateHome={handleNavigateHome}
            onCloseMobile={handleCloseMobile}
            onToggleCollapse={handleToggleCollapse}
          />
        </SheetContent>
      </Sheet>
    )
  }

  // Desktop sidebar
  return (
    <TooltipProvider delayDuration={200}>
      {/* Spacer div to reserve layout space - expands on hover to push content */}
      <div
        className={cn(
          'h-screen shrink-0 hidden md:block transition-all duration-300',
          sidebarWidthClassName,
        )}
      />
      {/* Actual sidebar - fixed positioning for smooth transitions */}
      <aside
        className={cn(
          'h-screen bg-sidebar text-sidebar-foreground flex flex-col transition-all duration-300 fixed top-0 left-0 z-[60] hidden md:flex',
          sidebarWidthClassName,
        )}
        onMouseEnter={() => {
          if (collapsed) setHovered(true)
        }}
        onMouseLeave={() => {
          if (collapsed) setHovered(false)
        }}
        aria-label={t('nav.mainNavigation')}
      >
        <SidebarContent
          showExpanded={showExpanded}
          navItems={navItems}
          currentPathname={location.pathname}
          collapsed={collapsed}
          onNavigateHome={handleNavigateHome}
          onCloseMobile={handleCloseMobile}
          onToggleCollapse={handleToggleCollapse}
        />
      </aside>
    </TooltipProvider>
  )
}
